import React, { useEffect, useMemo, useRef, useState } from "react";
import type { CheckRow } from "../types";

function fmt(tsUnix: number) {
  return new Date(tsUnix * 1000).toLocaleString();
}

function tipText(c: CheckRow | null, tsUnix: number) {
  if (!c) {
    return `${fmt(tsUnix)} • No Data `;
  }
  const status = c.ok ? "OK" : "DOWN";
  const code = c.status_code ? `code ${c.status_code}` : "code —";
  const lat = `${c.latency_ms ?? "—"}ms`;
  const err = c.err ? ` • ${c.err}` : "";
  return `${fmt(c.ts_unix)} • ${status} • ${code} • ${lat}${err}`;
}

export type BarSlot = {
  id: string;
  check: CheckRow | null;
  tsUnix: number;
};

type BucketPick = {
  c: CheckRow;
  dist: number;
};

export function createTimeSlots(checks: CheckRow[], slotCount: number, interval = 60): BarSlot[] {
  if (!checks || checks.length === 0) {
    // 데이터 없으면 현재 시간 기준
    const nowUnix = Math.floor(Date.now() / 1000);
    const end = Math.floor(nowUnix / interval) * interval;
    const start = end - (slotCount - 1) * interval;

    const result: BarSlot[] = [];
    for (let ts = start; ts <= end; ts += interval) {
      result.push({ id: `slot-${ts}`, check: null, tsUnix: ts });
    }
    return result;
  }

  // 마지막 체크 시점을 끝으로 설정 (현재 시간 X)
  const lastCheckTime = Math.max(...checks.map(c => c.ts_unix));
  const end = Math.floor(lastCheckTime / interval) * interval;
  const start = end - (slotCount - 1) * interval;

  // 체크들을 슬롯으로 버킷팅 (각 슬롯에 가장 가까운 체크만)
  const picks = new Map<number, BucketPick>();
  for (const c of checks) {
    const bucket = Math.floor(c.ts_unix / interval) * interval;
    const center = bucket + interval / 2;
    const dist = Math.abs(c.ts_unix - center);

    const prev = picks.get(bucket);
    if (!prev || dist < prev.dist) {
      picks.set(bucket, { c, dist });
    }
  }

  // 고정 개수 슬롯 생성
  const result: BarSlot[] = [];
  for (let ts = start; ts <= end; ts += interval) {
    result.push({
      id: `slot-${ts}`,
      check: picks.get(ts)?.c ?? null,
      tsUnix: ts,
    });
  }

  return result;
}

export function ChecksBars({ checks }: { checks: CheckRow[] }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<{ slot: BarSlot; x: number; y: number } | null>(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const displayLimit = windowWidth >= 1099 ? 65 : 28;
  const slots = useMemo(() => createTimeSlots(checks, displayLimit), [checks, displayLimit]);

  if (!slots.length) return <div className="muted">No checks yet</div>;

  return (
    <div className="barsShell" ref={wrapRef}>
      {/* <div className="barsMeta">Recent {displayLimit} status</div> */}
      <div className="barsMeta">Recent status</div>
      <div className="bars" aria-label="recent checks bars">
        {slots.map((slot) => (
          <div
            key={slot.id}
            className={
              slot.check === null
                ? "bar bar-missing"
                : slot.check.ok
                ? "bar bar-ok"
                : "bar bar-down"
            }
            onMouseEnter={(e) => {
              const rect = wrapRef.current?.getBoundingClientRect();
              const x = rect ? e.clientX - rect.left : 0;
              const y = rect ? e.clientY - rect.top : 0;
              setHover({ slot, x, y });
            }}
            onMouseMove={(e) => {
              const rect = wrapRef.current?.getBoundingClientRect();
              const x = rect ? e.clientX - rect.left : 0;
              const y = rect ? e.clientY - rect.top : 0;
              setHover((prev) => (prev ? { ...prev, x, y } : null));
            }}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </div>

      {hover && (() => {
        const wrapWidth = wrapRef.current?.offsetWidth || 0;
        const tooltipWidth = 300; // 툴팁 예상 너비
        const halfWidth = tooltipWidth / 2;

        // 툴팁 위치 clamp (카드를 벗어나지 않도록)
        const minLeft = halfWidth;
        const maxLeft = wrapWidth - halfWidth;
        const clampedLeft = clamp(hover.x, minLeft, maxLeft);

        // 화살표는 원래 hover 위치를 가리켜야 함
        // 화살표의 left는 툴팁 중앙(50%)에서 (원래위치 - clamp된위치)만큼 조정
        const arrowOffset = hover.x - clampedLeft;

        return (
          <div
            className="barTooltip"
            style={{
              left: clampedLeft,
              top: hover.y + 20,
            }}
          >
            {tipText(hover.slot.check, hover.slot.tsUnix)}
            <div
              className="barTooltipArrow"
              style={{
                left: `calc(50% + ${arrowOffset}px)`
              }}
            />
          </div>
        );
      })()}
    </div>
  );
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}
